// lobby-controller.ts — the HTML/DOM MULTIPLAYER flow, LOGIC half (HTML/DOM UI
// shell plan, Phase 3). A headless screen state machine that drives the SAME
// `XTransport` verbs the canvas MP forms drive (chooseserver/login/lobby/room/
// gameform) and implements the same `ILobbyCallback`/`IRoomCallback`/
// `IGameCallback` — so the wire it emits is byte-identical to the forms (proven
// by T2, `test/client/dom-lobby.test.ts`, against the scripted server). It
// reaches the SAME `BeginGameInfo` → `runSimulation` seam the form path reaches
// (the embedder, GameHost, runs the MP Sim). No DOM here — `lobby-view.ts`
// renders this state and routes actions back. Pure-data state for node-testing.
//
// Reuses unchanged: XTransport (the wire), LoginHandler (login/token),
// rosterFromPlayersUpdate (the wire→game roster map), Chatter, the protocol
// codec, and LobbyShellDeps (httpGet/makeTransport/cgDeps/runSimulation/env).

import {
  LoginHandler,
  knLoginResultNoPassword,
  knLoginResultAuthDown,
  knLoginResultNoAnons,
  knLoginResultAccountInUse,
} from '../ui/loginhandler.ts';
import { queryEncode } from '../ui/loginhandler.ts';
import { Chatter } from '../ui/chatter.ts';
import {
  rosterFromPlayersUpdate,
  GetColorName,
  GetDisplayName,
  type RosterEntry,
} from '../ui/gameform.ts';
import { kfPlrrReady, kfPlrrComputer, kfPlrrUnfulfilled, kfPlrrBot } from '../ui/gameform.ts';
import {
  type GameInfo,
  gameInfoDescription,
  knLeaveRoomHintJoinGame,
  knRoomJoinResultWrongPassword,
  knRoomCreateGameResultUpgradeMissionPack,
} from '../ui/roomform.ts';
import type { BeginGameInfo, LobbyShellDeps } from '../ui/lobby.ts';
import { kfRoleMultiplayer, kfRoleCreator } from '../ui/gamesession.ts';
import {
  XTransport,
  webSocketConnector,
  knTransportOpenResultSuccess,
  knTransportOpenResultProtocolMismatch,
  knLoginResultSuccess,
  knLoginResultAnonymousSuccess,
  knLoginResultFail,
  knLobbyJoinResultSuccess,
  knLobbyCreateRoomResultSuccess,
  knRoomJoinResultSuccess,
  knRoomJoinResultFail,
  knRoomCreateGameResultSuccess,
  knGameJoinResultSuccess,
  type ILobbyCallback,
  type IRoomCallback,
  type IGameCallback,
} from '../net/xtransport.ts';
import { kszServerInfoUrl, type ServerInfo } from '../ui/chooseserverform.ts';
import {
  CreateMissionList,
  GetString as PackInfoString,
  kmltMultiplayer,
  kmtMultiplayerChallenge,
  kmtMultiplayerAddOn,
  type MissionType,
  type PackInfoManagerSeam,
} from '../ui/missionpick.ts';
import { knVersionSimulation } from '../ui/creategameform.ts';
import { PACKID_MAIN } from '../io/savegame.ts';
import { kszPrefUsername, kszPrefToken, kszPrefPassword, kfPrefAnonymous } from '../io/prefs.ts';
import { PROTOCOL_CURRENT, NMID } from '../../../protocol/constants.ts';
import type { GameParams, PlayerRecord, GameInner } from '../../../protocol/gamemsg.ts';
import type { MissionIdentifier, PackId } from '../io/savegame.ts';

export type LobbyScreen =
  | 'connecting'
  | 'chooseserver'
  | 'login'
  | 'lobby'
  | 'createroom'
  | 'room'
  | 'creategame'
  | 'staging'
  | 'error';

export interface RoomRow {
  roomid: number;
  name: string;
  priv: boolean;
  cPlayers: number;
  cGames: number;
}
export interface GameRow {
  gameid: number;
  creator: string;
  title: string;
  cur: number;
  max: number;
  min: number;
}
export interface StagingPlayer {
  pid: number;
  name: string;
  side: number;
  ready: boolean;
  bot: boolean;
  computer: boolean;
  me: boolean;
}
/** the CreateGameForm Challenge|Add-On radio-tab category (creategameform.cpp
 *  IndexFromMissionType): the MP mission lists are split into these two tabs. */
export type MpMissionCategory = 'challenge' | 'addon';
export interface MpMissionRow {
  index: number;
  lvl: string;
  title: string;
  miid: MissionIdentifier;
  /** the supported player range (MissionDescription cPlayersMin/cPlayersMax) —
   *  shown after the title like the CreateGameForm list (creategameform.cpp:206-280). */
  cPlayersMin: number;
  cPlayersMax: number;
  /** which CreateGameForm category tab this mission falls under. */
  category: MpMissionCategory;
  /** "<title> by <author>" (or "<pack> by Spiffcode, Inc." for the main pack) —
   *  the CreateGameForm kidcMissionPackInfo label (creategameform.cpp:439-474). */
  info: string;
}

/** game/dlmissionpack.cpp AskInstallMissionPack over the DOM: the host wires
 *  this to the SAME gphttp/gppackm/gppim DLC stack the ADD-ON MISSION PACKS menu
 *  (startDomDlc) uses. The C++ pops a Yes/No "must install/upgrade the mission
 *  pack first — download now?" query, then the DownloadBox; this seam owns that
 *  whole DOM dialog and resolves once the pack is (or isn't) installed. */
export interface InstallMissionPackResult {
  /** true = the pack is installed-and-current now (the C++ bool return). */
  installed: boolean;
  /** for the create-game UPGRADE arm: the adopted upgraded pack id the retry
   *  re-advertises with (game/roomform.cpp:218 params.packid = packidUpgrade). */
  packid?: PackId;
}

export interface LobbyControllerDeps {
  /** the SAME bundle makePlayMultiplayer uses — gameHost.lobbyDeps(). */
  deps: LobbyShellDeps;
  /** re-render on every state change. */
  onChange: () => void;
  /** game/dlmissionpack.cpp:71-106 AskInstallMissionPack — prompt to download a
   *  missing/out-of-date mission pack on MP join/create, returning whether it's
   *  installed-and-current afterwards. Absent (node tests / main-pack-only) ⇒ the
   *  controller treats every pack as already installed (the main pack always is). */
  installMissionPack?(
    packid: PackId,
    uiTitle: string,
    packTitle: string | null,
  ): Promise<InstallMissionPackResult>;
  /** telemetry sink (main.ts wires web/telemetry.track) — absent in node tests. */
  telemetry?(kind: string, data?: Record<string, unknown>): void;
  /** the ACCOUNTINUSE query the canvas LoginForm popped (loginform.cpp:219-227): "Your account
   *  is already logged in to this server with another device. Would you like to disconnect that
   *  device and then login?" → Yes/No. Absent (node tests) ⇒ treated as No (never auto-kick). */
  confirmDisconnectShared?(): Promise<boolean>;
}

const kcbPlayerName = 32;

export class LobbyController implements ILobbyCallback, IRoomCallback, IGameCallback {
  screen: LobbyScreen = 'connecting';
  inGame = false;
  status = 'Connecting…';
  error = '';
  /** the error screen is a PROTOCOL MISMATCH (#206) — this build is older than
   *  the server. The view adds a one-tap RELOAD next to BACK when set. */
  needsUpdate = false;
  serverName = '';
  playerName = 'anonymous';
  /** whether THIS session signed in anonymously (login(anonymous=true)) — used to
   *  explain a registered-only room rejection (see initiateJoinRoom). */
  anonymous = false;
  isCreator = false;
  /** the lobby occupant ("lurker") count (OnLurkerCount) — display only. */
  lurkerCount = 0;
  /** the most recent lobby chat line (game/lobbyform.cpp:383-386 — the LOBBY has
   *  NO chat panel; an incoming line shows as a transient message, not a log).
   *  The view surfaces this as a one-line notice, the canvas msgbox's DOM analog. */
  lobbyChat: string[] = [];
  /** unread room/game chat — set when a chat line arrives while the overlay is
   *  closed, cleared when it's opened. Drives the CHAT-button blink/notify the
   *  canvas Chatter does (chatter.cpp:94 StartBlinking → gameform/roomform
   *  OnChatBlink shows the CHAT button). True ⇒ the view blinks/badges CHAT. */
  chatUnread = false;
  /** a pending "Enter Password:" prompt for a private room (the C++
   *  HostInitiateAsk; game/lobbyform.cpp:131-134). Null = no prompt. Holds the
   *  target room and a hint shown when a prior attempt's password was wrong. */
  passwordPrompt: { roomid: number; name: string; wrong: boolean } | null = null;

  private readonly d: LobbyControllerDeps;
  private tra: XTransport | null = null;
  /** the usable server list (the ChooseServerForm rows) when >1 server is
   *  advertised — the player picks one before login. */
  private serverList: ServerInfo[] = [];
  private handler: LoginHandler | null = null;
  private chatter: Chatter | null = null;
  private readonly rooms = new Map<number, RoomRow>();
  private readonly games = new Map<number, GameInfo>();
  private currentRoom: RoomRow | null = null;
  private currentRoomPassword = ''; // remembered so the post-game room re-join works
  private currentGame: GameInfo | null = null;
  /** the room occupant list (roomform.cpp players_) — drives the "X entered/left
   *  the room" system chat. status_ gates the initial snapshot (no chat until
   *  OnStatusComplete; roomform.cpp:549-559). */
  private roomPlayers: string[] = [];
  private roomStatusComplete = false;
  /** the previous staging roster's ready snapshot (gameform.cpp GetPlayerReadies)
   *  — diffed on each ScPlayersUpdate to emit join/leave/READY system lines. */
  private prevReadies: { name: string; side: number; ready: boolean; bot: boolean }[] = [];
  private lastPlayersUpdate: PlayerRecord[] = [];
  private gameRoster: RosterEntry[] = [];
  /** the staging roster's currently-selected removable bot (its pid), or null. Selecting a bot row flips
   *  the ADD OPPONENT BOT button to REMOVE BOT (creator only). */
  private selectedBotPid_: number | null = null;
  private gameSeed = 0;
  private serverParams: GameParams | null = null;
  /** the knLoginResult* from the most recent attemptLogin() (undefined before any) — read by
   *  attemptAutoLogin for the dead-token re-mint + MY PROFILE prefs sync. */
  private lastLoginResult: number | undefined = undefined;

  constructor(d: LobbyControllerDeps) {
    this.d = d;
  }

  // ── observable state for the view ───────────────────────────────────────────
  /** the CHOOSE SERVER list (name/location/status/player_count) — the filtered,
   *  sort_key-ordered ServerInfo[] when more than one server is advertised. */
  servers(): ServerInfo[] {
    return this.serverList;
  }
  roomList(): RoomRow[] {
    return [...this.rooms.values()].sort((a, b) => b.roomid - a.roomid);
  }
  gameList(): GameRow[] {
    return [...this.games.values()]
      .sort((a, b) => b.gameid - a.gameid)
      .map((g) => ({
        gameid: g.gameid,
        creator: g.creator,
        title: g.title,
        cur: g.anames.length,
        max: g.maxplayers,
        min: g.minplayers,
      }));
  }
  roster(): StagingPlayer[] {
    const me = this.playerName;
    // gameform.cpp RefreshPlayerList iterates humans() — unfulfilled (empty) slots are NOT listed (they
    // surface as the "N more players needed" row, playersNeeded()), and a map's BUILT-IN AI (computer/
    // overmind slots, e.g. Desert River Riches) is excluded too (gameform.ts humans() drops kfPlrComputer)
    // so it never shows as a "ready" player/bot. Bots added via ADD OPPONENT BOT occupy a HUMAN slot
    // (platform 'bot' → kfPlrrBot, never kfPlrrComputer), so they stay listed.
    return this.lastPlayersUpdate
      .filter((p) => (p.wf & kfPlrrUnfulfilled) === 0 && (p.wf & kfPlrrComputer) === 0)
      .map((p) => ({
        pid: p.pid,
        name: p.name,
        side: p.side,
        ready: (p.wf & kfPlrrReady) !== 0,
        bot: (p.wf & kfPlrrBot) !== 0,
        computer: (p.wf & kfPlrrComputer) !== 0,
        me: p.name === me,
      }));
  }
  /** gameform.cpp:632-644 GetPlayerCountNeeded(false) — how many more (fulfilled,
   *  non-computer) players are still needed to reach minplayers. 0 = enough.
   *  Drives the staging roster's "N more player(s) needed" row. */
  playersNeeded(): number {
    let cTotal = 0;
    for (const p of this.lastPlayersUpdate) {
      if (p.wf & kfPlrrUnfulfilled) continue;
      if (p.wf & kfPlrrComputer) continue;
      cTotal++;
    }
    const min = this.currentGame?.minplayers ?? 2;
    const cNeeded = min - cTotal;
    return cNeeded <= 0 ? 0 : cNeeded;
  }
  /** gameform.cpp:630-643 GetOpenSlotCount — joinable slots still open = maxplayers minus the filled
   *  human+bot slots (a level's computer/AI slots don't occupy a joinable slot). 0 = full (no room to add
   *  a bot → the staging form hides ADD OPPONENT BOT). roster() is already the non-unfulfilled,
   *  non-computer set, so its length is the filled human+bot count. */
  openSlots(): number {
    const cOpen = this.maxPlayers() - this.roster().length;
    return cOpen < 0 ? 0 : cOpen;
  }
  roomName(): string {
    return this.currentRoom?.name ?? '';
  }
  gameTitle(): string {
    return this.currentGame?.title ?? '';
  }
  /** the staging-form detail labels (GameForm: Map/Game Speed/# players), read
   *  from the authoritative ScGameParams (serverParams) with the OnAddGame
   *  GameInfo as the fallback before the params arrive. Display only. */
  mapName(): string {
    return this.serverParams?.szLvlFilename ?? this.currentGame?.params.szLvlFilename ?? '';
  }
  gameSpeed(): number {
    return this.serverParams?.tGameSpeed ?? this.currentGame?.params.tGameSpeed ?? 0;
  }
  maxPlayers(): number {
    return this.currentGame?.maxplayers ?? 0;
  }
  canBegin(): boolean {
    const r = this.roster();
    return this.isCreator && r.length >= (this.currentGame?.minplayers ?? 2);
  }

  // ── the flow ────────────────────────────────────────────────────────────────
  /** Begin the MP flow: open a connection, then PAUSE for the player to sign in.
   *  `opts.transport` injects a transport (tests/scripted server) — it Opens and
   *  lands on the LOGIN screen. Otherwise fetch the serverinfo list and, like the
   *  canvas ChooseServerForm, auto-connect a lone server (→ login) or present the
   *  CHOOSE SERVER list when more than one is usable. */
  async start(opts?: { transport?: XTransport }): Promise<void> {
    this.screen = 'connecting';
    this.status = 'Contacting service…';
    this.error = '';
    this.needsUpdate = false;
    this.serverList = [];
    this.d.onChange();
    try {
      if (opts?.transport !== undefined) {
        const tra = opts.transport;
        const rc = await tra.Open();
        if (rc !== knTransportOpenResultSuccess) {
          this.failOpen(rc);
          return;
        }
        this.tra = tra;
        this.d.deps.mp.tra = tra;
        await this.attemptAutoLogin();
        return;
      }

      const servers = await this.fetchServers();
      if (servers === null) {
        this.fail(this.error || 'Could not connect to a server.');
        return;
      }
      if (servers.length === 1) {
        await this.connect(servers[0]);
        return;
      } // the canvas auto-connects a lone server
      // more than one usable server — let the player choose (ChooseServerForm)
      this.serverList = servers;
      this.screen = 'chooseserver';
      this.status = '';
      this.d.onChange();
    } catch (e) {
      this.fail(`Multiplayer error: ${(e as Error).message}`);
    }
  }

  /** CONNECT (CHOOSE SERVER): build a transport for the chosen server, Open it,
   *  and advance to LOGIN — the ChooseServerForm.Connect path. */
  async selectServer(index: number): Promise<void> {
    const info = this.serverList[index];
    if (info === undefined) return;
    await this.connect(info);
  }

  /** Open a transport against `info` then advance to LOGIN (or fail). */
  private async connect(info: ServerInfo): Promise<void> {
    this.screen = 'connecting';
    this.status = `Connecting to ${info.name}…`;
    this.d.onChange();
    const d = this.d.deps;
    const tra = (
      d.makeTransport ??
      ((i: ServerInfo) => new XTransport({ connect: webSocketConnector(i.websocket_proxy) }))
    )(info);
    const rc = await tra.Open();
    if (rc !== knTransportOpenResultSuccess) {
      this.failOpen(rc);
      return;
    }
    this.tra = tra;
    this.d.deps.mp.tra = tra;
    this.serverName = info.name;
    await this.attemptAutoLogin();
  }

  /** LOGIN (the MultiplayerLoginForm OK arm): sign in (anonymous or with a
   *  name/password) then JoinLobby. On failure the player stays on LOGIN. */
  async login(anonymous: boolean, name = '', password = ''): Promise<void> {
    if (this.tra === null) {
      this.fail('Not connected to a server.');
      return;
    }
    this.status = 'Signing in…';
    this.d.onChange();
    try {
      this.handler = this.newHandler();
      this.anonymous = anonymous;
      this.handler.SetAnonymous(anonymous);
      if (!anonymous) {
        this.handler.SetUsername(name);
        this.handler.SetPassword(password);
      }
      if (await this.attemptLogin(anonymous)) await this.joinLobbyAfterLogin();
      else this.d.onChange(); // stay on LOGIN with the failure status attemptLogin set
    } catch (e) {
      this.fail(`Multiplayer error: ${(e as Error).message}`);
    }
  }

  /** Sign in automatically with the saved (non-anonymous) credentials so a
   *  player who already signed in — at MY PROFILE or a prior MP session — isn't
   *  asked to sign in again every time they enter multiplayer (the saved token/
   *  password persist in prefs across app restarts, so this survives a reload).
   *  This is the canvas LoginForm's fAutoLogin arm (loginform.cpp/loginform.ts:98):
   *  run the SAME AttemptLogin — so a signed-in player lands straight in the lobby,
   *  an ACCOUNTINUSE gets the same "disconnect the other device?" prompt, and any
   *  other failure drops to the LOGIN form (pre-filled with the remembered name)
   *  showing the exact reason. */
  private async attemptAutoLogin(): Promise<void> {
    const prefs = this.d.deps.env.prefs;
    if (this.tra === null || !this.hasSavedLogin()) {
      this.screen = 'login';
      this.status = '';
      this.d.onChange();
      return;
    }
    this.status = 'Signing in…';
    this.d.onChange();
    this.anonymous = false;
    let ok = false;
    try {
      this.handler = this.newHandler(); // InitFromGlobals loads the saved username/password/token
      this.handler.SetAnonymous(false);
      ok = await this.attemptLogin(false);
      // Login() self-heals only a STALE token (re-mint via the saved password + one retry). A
      // token rejected any other way (e.g. minted under a rotated secret → FAIL) aborts without
      // ever trying the password — so if the first attempt failed and we HAVE a password, clear
      // the dead token and attempt once more down the GetToken(password) path.
      if (!ok && prefs.GetString(kszPrefPassword) !== '' && this.lastLoginResult !== undefined) {
        const stillHasToken = prefs.GetString(kszPrefToken) !== '';
        const rejected =
          this.lastLoginResult === knLoginResultFail ||
          this.lastLoginResult === knLoginResultAnonymousSuccess;
        if (stillHasToken && rejected) {
          prefs.SetString(kszPrefToken, '');
          this.handler = this.newHandler();
          this.handler.SetAnonymous(false);
          ok = await this.attemptLogin(false);
        }
      }
    } catch {
      /* fall through to the LOGIN form */
    }
    if (ok) {
      await this.joinLobbyAfterLogin();
      return;
    }
    // Couldn't re-authenticate — drop to the LOGIN form (pre-filled). When the saved identity is
    // definitively DEAD (a stale token we can't re-mint — no/wrong password), clear the token so
    // MY PROFILE stops claiming a sign-in multiplayer can't honor (the two screens must agree);
    // the username is kept for the form pre-fill. Transient failures (auth down, transport error,
    // or the user declining the disconnect prompt) keep the credentials for the next entry.
    const dead =
      this.lastLoginResult === knLoginResultFail ||
      this.lastLoginResult === knLoginResultNoPassword ||
      this.lastLoginResult === knLoginResultAnonymousSuccess;
    if (dead) {
      // Genuinely rejected (NOT a transient AuthDown/transport error, which are excluded above) —
      // the saved credentials can't authenticate. Clear BOTH token and password so every sign-in
      // path agrees you're signed out: currentStatsPlayerName (MY PROFILE + leaderboard) keys off
      // the token, hasSavedLogin (MP auto-login) off token-OR-password. Clearing only the token
      // would leave MP auto-retrying a dead password on every entry while MY PROFILE already shows
      // signed-out — the two screens must agree. No UX cost: the LOGIN form never pre-fills the
      // password field, so a re-sign-in re-types it regardless; the username is kept to pre-fill.
      prefs.SetString(kszPrefToken, '');
      prefs.SetString(kszPrefPassword, '');
      this.d.deps.env.savePreferences();
    }
    this.d.telemetry?.('mp_autologin_failed', { why: `result ${this.lastLoginResult}`, dead });
    this.screen = 'login';
    // attemptLogin set a specific reason for ACCOUNTINUSE/AuthDown/etc.; give the plain
    // stale-token case (no message) the generic re-prompt line.
    if (this.status === 'Signing in…' || this.status.length === 0) {
      this.status = 'Automatic sign-in failed — please sign in again.';
    }
    this.d.onChange();
  }

  /** The canvas LoginForm.AttemptLogin (loginform.cpp:186-244 / loginform.ts:221): run
   *  handler.Login() and map the result to the ORIGINAL game's behavior — success ⇒ true;
   *  ACCOUNTINUSE ⇒ ASK "disconnect the other device and login?" and, only on Yes, send
   *  DISCONNECTSHAREDACCOUNTS then retry; every other failure ⇒ set the exact C++ status
   *  message ⇒ false. Records the last result in `lastLoginResult` for the auto-login
   *  post-processing (dead-token re-mint + prefs sync). */
  private async attemptLogin(anonymous: boolean): Promise<boolean> {
    if (this.handler === null) return false;
    const lr = await this.handler.Login();
    this.lastLoginResult = lr;
    switch (lr) {
      case knLoginResultSuccess:
        return true;

      case knLoginResultAnonymousSuccess:
        // The canvas returned true here for any attempt, but on a NAMED attempt AnonymousSuccess
        // is the LoginHandler wrong-password quirk: a stale saved token + a failed re-mint retries
        // with an EMPTY token, which the server accepts as anonymous. Silently entering as anonNN
        // when the player asked for their account is a latent original-game bug — treat a named
        // attempt as a failure (an anonymous attempt is the real thing).
        if (anonymous) return true;
        this.status =
          'Your login attempt failed. Please check your username and password, or log in anonymously.';
        return false;

      case knLoginResultNoPassword:
        this.status = 'The password field is empty. Please enter your password and try again.';
        return false;

      case knLoginResultAuthDown:
        this.status =
          'The player authentication server is not responding. Please try to login as an anonymous player.';
        return false;

      case knLoginResultNoAnons:
        this.status = 'This server does not allow anonymous logins. Please login with an account.';
        return false;

      case knLoginResultAccountInUse: {
        // loginform.cpp:219-227 — the "already logged in with another device" query. Yes ⇒
        // DisconnectSharedAccounts (the server disposes only OTHER endpoints with this account,
        // and processes the kick before the retried LOGIN — per-connection order — so the retry
        // is race-free) ⇒ AttemptLogin again. No ⇒ leave the player on the form.
        this.d.telemetry?.('mp_login_accountinuse', {});
        const yes =
          this.d.confirmDisconnectShared !== undefined
            ? await this.d.confirmDisconnectShared()
            : false;
        if (yes && this.tra !== null) {
          this.tra.DisconnectSharedAccounts();
          return this.attemptLogin(anonymous); // the failed attempt cleared the token → GetToken path
        }
        this.status = 'This account is already signed in from another device.';
        return false;
      }

      default:
        // knLoginResultFail (and any unexpected code): the canvas showed a generic failure.
        this.status = anonymous
          ? 'An unknown error occurred while logging in. Please try again.'
          : 'Your login attempt failed. Please check your username and password, or log in anonymously.';
        return false;
    }
  }

  /** The shared post-login tail (manual + auto): record the player name and join
   *  the lobby, landing on the lobby screen (or the error screen on failure). */
  private async joinLobbyAfterLogin(): Promise<void> {
    if (this.tra === null || this.handler === null) {
      this.fail('Not connected to a server.');
      return;
    }
    this.playerName = this.handler.GetPlayerName();
    this.status = 'Entering lobby…';
    this.d.onChange();
    if ((await this.tra.JoinLobby(this)) !== knLobbyJoinResultSuccess) {
      this.fail('Could not enter the lobby.');
      return;
    }
    this.screen = 'lobby';
    this.status = '';
    this.d.onChange();
  }

  /** True when prefs hold a signed-in REAL (non-anonymous) account we can re-auth
   *  silently: a saved username plus a token or password, not flagged anonymous.
   *  Mirrors currentStatsPlayerName (main.ts) — the same "signed in" signal the
   *  leaderboard / MY PROFILE use. */
  private hasSavedLogin(): boolean {
    const prefs = this.d.deps.env.prefs;
    if (prefs.GetBool(kfPrefAnonymous)) return false;
    if (prefs.GetString(kszPrefUsername).trim() === '') return false;
    return prefs.GetString(kszPrefToken) !== '' || prefs.GetString(kszPrefPassword) !== '';
  }

  /** The remembered non-anonymous username for the LOGIN form to pre-fill ('' when
   *  none / last signed in anonymously). The view defaults to the named-login tab
   *  when this is set, so a returning player just re-enters their password. */
  savedUsername(): string {
    const prefs = this.d.deps.env.prefs;
    if (prefs.GetBool(kfPrefAnonymous)) return '';
    return prefs.GetString(kszPrefUsername).trim();
  }

  /** Clear a stale status line (e.g. a prior "Sign-in failed…") with no other side effects —
   *  the login view calls this when the user toggles the anonymous box so an old failure
   *  doesn't linger over the form (and the new banner never sits across the LOGIN button). */
  clearStatus(): void {
    if (this.status.length > 0) {
      this.status = '';
      this.d.onChange();
    }
  }

  /** SIGN OUT (from the lobby): leave the lobby, SignOut on the wire, and return
   *  to the LOGIN form on the SAME connection (the canvas LobbyForm SIGN OUT). */
  async signOut(): Promise<void> {
    if (this.tra === null) {
      this.screen = 'login';
      this.d.onChange();
      return;
    }
    this.tra.LeaveLobby();
    if (this.handler !== null) await this.handler.SignOut();
    this.rooms.clear();
    this.playerName = 'anonymous';
    this.anonymous = true; // SignOut reverts the endpoint to anonymous (endpoint.cpp:OnSignOut)
    this.screen = 'login';
    this.status = '';
    this.d.onChange();
  }

  /** CREATE ROOM (LobbyForm): open the create-room form (the view collects the
   *  name/password). CANCEL returns to the lobby. */
  openCreateRoom(): void {
    if (this.screen === 'lobby') {
      this.screen = 'createroom';
      this.d.onChange();
    }
  }
  cancelCreateRoom(): void {
    if (this.screen === 'createroom') {
      this.screen = 'lobby';
      this.d.onChange();
    }
  }

  /** CREATE GAME (RoomForm): open the create-game form (the view collects the
   *  map + game speed). CANCEL returns to the room. */
  openCreateGame(): void {
    if (this.screen === 'room') {
      this.screen = 'creategame';
      this.d.onChange();
    }
  }
  cancelCreateGame(): void {
    if (this.screen === 'creategame') {
      this.screen = 'room';
      this.d.onChange();
    }
  }

  async createRoom(name: string, password = ''): Promise<void> {
    if (this.tra === null) return;
    this.status = 'Creating room…';
    this.d.onChange();
    const { result, roomid } = await this.tra.CreateRoom(name, password);
    if (result !== knLobbyCreateRoomResultSuccess) {
      this.status = 'Could not create the room.';
      this.d.onChange();
      return;
    }
    await this.enterRoom(roomid, password);
  }

  // JOIN ROOM (LobbyForm.OnJoinRoom; game/lobbyform.cpp:124-138): a private room
  // first prompts for its password (HostInitiateAsk) before any join attempt.
  async joinRoom(roomid: number, password = ''): Promise<void> {
    const room = this.rooms.get(roomid);
    if (room !== undefined && room.priv && password === '') {
      this.passwordPrompt = { roomid, name: room.name, wrong: false };
      this.d.onChange();
      return;
    }
    await this.initiateJoinRoom(roomid, password);
  }

  /** OK on the "Enter Password:" prompt → re-run the join with the entered
   *  password (the canvas OnFilterEvent(askStringEvent) → InitiateJoinRoom;
   *  game/lobbyform.cpp:403-417). */
  async submitJoinPassword(password: string): Promise<void> {
    const p = this.passwordPrompt;
    if (p === null) return;
    this.passwordPrompt = null;
    await this.initiateJoinRoom(p.roomid, password);
  }

  /** CANCEL on the "Enter Password:" prompt → back to the room list, no join. */
  cancelJoinPassword(): void {
    this.passwordPrompt = null;
    this.d.onChange();
  }

  // InitiateJoinRoom (game/lobbyform.cpp:204-225): CanJoinRoom (still in the
  // LOBBY) gives early feedback. A wrong password re-prompts (the C++ OnJoinRoom
  // loop); a clean Success proceeds to the actual JoinRoom (which leaves the
  // lobby). Any other failure surfaces the canvas join message.
  private async initiateJoinRoom(roomid: number, password: string): Promise<void> {
    if (this.tra === null) return;
    const result = await this.tra.CanJoinRoom(roomid, password);
    if (result === knRoomJoinResultWrongPassword) {
      const room = this.rooms.get(roomid);
      this.passwordPrompt = { roomid, name: room?.name ?? '', wrong: true };
      this.d.onChange();
      return;
    }
    if (result !== knRoomJoinResultSuccess) {
      // A registered-only room ("Main / Registered", "Main / Unmoderated") rejects
      // an anonymous user with knRoomJoinResultFail (room.ts canAddPlayer). The wire
      // code is the SAME Fail the original C++ server sent — kept byte-identical for
      // wis parity (test/harness/registered.livediff) — so the reason can't be read
      // from the code alone. But for an anonymous session a Fail here is always the
      // registered-only gate (the only other Fail, a /kick re-join ban, can't precede
      // a first join), so surface the actionable message instead of the generic one.
      this.status =
        this.anonymous && result === knRoomJoinResultFail
          ? 'This room is for registered players. Sign in to join.'
          : roomJoinMessage(result);
      this.d.onChange();
      return;
    }
    await this.enterRoom(roomid, password);
  }

  private async enterRoom(roomid: number, password: string): Promise<void> {
    if (this.tra === null) return;
    this.status = 'Joining room…';
    this.d.onChange();
    this.games.clear();
    // leave the lobby first (LOBBY→LOGGEDIN) — JoinRoom requires LOGGEDIN, exactly
    // as the canvas LobbyForm leaves the lobby before RoomForm joins.
    this.tra.LeaveLobby();
    if ((await this.tra.JoinRoom(roomid, password, this)) !== knRoomJoinResultSuccess) {
      this.status = 'Could not join the room.';
      this.d.onChange();
      return;
    }
    this.currentRoom = this.rooms.get(roomid) ?? {
      roomid,
      name: '',
      priv: false,
      cPlayers: 0,
      cGames: 0,
    };
    this.currentRoomPassword = password;
    this.chatter = new Chatter({
      chatc: this.d.deps.chatc,
      sndm: this.d.deps.env.sndm,
      gtimm: this.d.deps.env.gtimm,
      getPlayerName: () => this.playerName,
      sendChat: (c) => {
        this.tra?.SendChat(c);
      },
    });
    this.chatter.SetChatTitle('Room Chat'); // roomform.cpp:152 — set the title + clear the log
    // roomform.cpp:226-227 — seed players_ with this player (status_ still true so
    // no chat yet); the OnAddPlayer/OnStatusComplete stream fills the rest.
    this.chatUnread = false;
    this.roomPlayers = [this.playerName];
    this.roomStatusComplete = false;
    this.prevReadies = [];
    this.screen = 'room';
    this.status = '';
    this.d.onChange();
  }

  async leaveRoom(): Promise<void> {
    if (this.tra === null) {
      this.screen = 'lobby';
      this.d.onChange();
      return;
    }
    await this.tra.LeaveRoom(0);
    this.chatter?.Dispose();
    this.chatter = null;
    this.currentRoom = null;
    this.games.clear();
    // LeaveRoom lands the transport in LOGGEDIN, not LOBBY. Re-join the lobby
    // (LOGGEDIN→LOBBY) so the room list refreshes and the next CanJoinRoom — which
    // requires LOBBY — succeeds. The canvas flow does this implicitly by re-running
    // LobbyForm.DoForm (which calls JoinLobby) on every return to the lobby; the flat
    // DOM controller must do it explicitly or leave→rejoin fails the LOBBY-state gate.
    if ((await this.tra.JoinLobby(this)) !== knLobbyJoinResultSuccess) {
      this.fail('Could not return to the lobby.');
      return;
    }
    this.screen = 'lobby';
    this.d.onChange();
  }

  /** The multiplayer mission list (createGame source) — CreateMissionList over
   *  kmltMultiplayer, the SAME enumeration CreateGameForm uses. Each row carries
   *  its Challenge|Add-On category (creategameform.cpp InitLists — the radio-tab
   *  split on md.mt) + the pack-author info string (UpdateLabels:439-474). */
  mpMissions(): MpMissionRow[] {
    const pml = CreateMissionList(this.d.deps.cgDeps.mlDeps, null, kmltMultiplayer);
    if (pml === null) return [];
    const ppim = this.d.deps.cgDeps.mlDeps.ppim;
    const out: MpMissionRow[] = [];
    for (let i = 0; i < pml.GetCount(); i++) {
      const md = pml.GetMissionDescription(i);
      const miid = pml.GetMissionIdentifier(i);
      if (md === null || miid === null) continue;
      // creategameform.cpp:280-282 — only the two MP categories are listed.
      if (md.mt !== kmtMultiplayerChallenge && md.mt !== kmtMultiplayerAddOn) continue;
      out.push({
        index: out.length,
        lvl: miid.szLvlFilename,
        title: md.szLvlTitle,
        miid,
        cPlayersMin: md.cPlayersMin,
        cPlayersMax: md.cPlayersMax,
        category: categoryOfMissionType(md.mt),
        info: missionPackInfo(ppim, miid.packid, md.szPackName),
      });
    }
    return out;
  }

  /** Create a game on the chosen MP mission (GameParams built exactly like
   *  CreateGameForm) then enter staging as the creator. */
  async createGame(miid: MissionIdentifier, tGameSpeed?: number): Promise<void> {
    if (this.tra === null || this.currentRoom === null) return;
    const params: GameParams = {
      packId: miid.packid.id,
      packHash: miid.packid.hash.slice(),
      dwVersionSimulation: knVersionSimulation,
      tGameSpeed: tGameSpeed ?? Math.max(4, this.d.deps.env.gameSpeed()),
      szLvlFilename: miid.szLvlFilename,
      filler: new Uint8Array(3),
    };
    this.status = 'Creating game…';
    this.d.onChange();
    // game/roomform.cpp:176-230 while(true): an UpgradeMissionPack result prompts
    // to download the upgrade, then re-advertises with the adopted packid.
    for (;;) {
      const { result, gameid, packidUpgrade } = await this.tra.CreateGame(params);
      if (result === knRoomCreateGameResultSuccess) {
        await this.enterStaging(gameid, true);
        return;
      }
      if (result === knRoomCreateGameResultUpgradeMissionPack) {
        const r = await this.askInstallMissionPack(packidUpgrade, 'Advertise Game', null);
        if (r.installed && r.packid !== undefined) {
          params.packId = r.packid.id;
          params.packHash = r.packid.hash.slice();
          continue; // retry the advertise with the upgraded pack (the C++ continue)
        }
        // declined / install failed → abort like the C++ false-arm break
        this.status = '';
        this.d.onChange();
        return;
      }
      this.status = 'Could not create the game.';
      this.d.onChange();
      return;
    }
  }

  // JOIN GAME (RoomForm.OnJoinGame; game/roomform.cpp:162-174): prompt to install
  // the game's mission pack first if it's missing, then join. A declined install
  // aborts the join (the C++ AskInstallMissionPack false return).
  async joinGame(gameid: number): Promise<void> {
    const info = this.games.get(gameid);
    if (info === undefined) return;
    const packid: PackId = { id: info.params.packId, hash: info.params.packHash };
    const { installed } = await this.askInstallMissionPack(packid, 'Join Game', info.title);
    if (!installed) {
      this.status = '';
      this.d.onChange();
      return;
    }
    await this.enterStaging(gameid, false);
  }

  private async enterStaging(gameid: number, creator: boolean): Promise<void> {
    if (this.tra === null || this.currentRoom === null) return;
    this.status = 'Joining game…';
    this.d.onChange();
    this.isCreator = creator;
    this.currentGame = this.games.get(gameid) ?? null;
    this.lastPlayersUpdate = [];
    this.gameRoster = [];
    this.selectedBotPid_ = null;
    this.gameSeed = 0;
    this.serverParams = null;
    // leave the room first (ROOM→LOGGEDIN) — JoinGame requires LOGGEDIN, exactly
    // as the canvas RoomForm leaves the room before GameForm joins the game.
    await this.tra.LeaveRoom(0);
    this.tra.SetGameCallback(this);
    if ((await this.tra.JoinGame(gameid, this.currentRoom.roomid)) !== knGameJoinResultSuccess) {
      // we already left the room (LOGGEDIN now) — re-join it so room actions work
      this.tra.SetGameCallback(null);
      this.status = 'Could not join the game.';
      await this.returnToRoom();
      return;
    }
    // gameform.cpp:284-286 — SetChatTitle('Game Chat') CLEARS the room log, then a
    // "<creator> created game <desc>." system line opens the game chat.
    this.chatter?.SetChatTitle('Game Chat');
    this.chatUnread = false;
    this.prevReadies = [];
    if (this.currentGame !== null) {
      this.pushSystemLine(
        `${this.currentGame.creator} created game ${gameInfoDescription(this.currentGame)}.`,
      );
    }
    this.screen = 'staging';
    this.status = 'Waiting for the game to start…';
    this.d.onChange();
  }

  async leaveGame(): Promise<void> {
    if (this.tra !== null) {
      this.tra.SetGameCallback(null);
      await this.tra.LeaveGame();
    }
    this.isCreator = false;
    this.currentGame = null;
    await this.returnToRoom();
  }

  /** After leaving/finishing a game we're back in LOGGEDIN (the room was left to
   *  join the game) — re-join the room, like the canvas roomLoop. */
  private async returnToRoom(): Promise<void> {
    const roomid = this.currentRoom?.roomid ?? -1;
    if (roomid >= 0) {
      await this.enterRoom(roomid, this.currentRoomPassword);
    } else {
      this.screen = 'lobby';
      this.d.onChange();
    }
  }

  /** READY (non-creator) / BEGIN (creator) — the GameForm OK arm. */
  begin(): void {
    if (this.tra === null) return;
    this.tra.SendNetMessage(
      this.isCreator
        ? { nmid: NMID.CsRequestBeginGame, kind: 'empty' }
        : { nmid: NMID.CsClientReady, kind: 'empty' },
    );
  }

  /** ADD OPPONENT BOT (creator-only) — the [[wi-bot]] CsAddBotRequest path. */
  addBot(): void {
    if (this.isCreator) this.tra?.RequestAddBot();
  }

  /** The staging roster's currently-selected removable bot (its pid), or null. The view flips the ADD
   *  OPPONENT BOT button to REMOVE BOT while this is set. */
  selectedBotPid(): number | null {
    return this.selectedBotPid_;
  }

  /** Select/deselect a bot roster row (creator-only) — re-selecting the same row clears it, so the button
   *  toggles back to ADD. Only bot slots are selectable (the view passes bot pids). */
  toggleBotSelection(pid: number): void {
    if (!this.isCreator) return;
    this.selectedBotPid_ = this.selectedBotPid_ === pid ? null : pid;
    this.d.onChange();
  }

  /** REMOVE BOT (creator-only) — CsRemoveBotRequest with the selected bot's staging-roster pid. The DO
   *  frees the slot and re-broadcasts ScPlayersUpdate (which clears the selection). */
  removeBot(pid: number): void {
    if (this.isCreator) this.tra?.RemoveBot(pid);
  }

  sendChat(text: string): void {
    if (text.length > 0) this.tra?.SendChat(text);
  }

  /** Open the shared full-screen chat overlay (the canvas CHAT button → Chatter.ShowChat).
   *  Opening clears the unread badge (chatter.cpp ShowChat → StopBlinking). */
  showChat(): void {
    this.chatter?.ShowChat();
    this.chatUnread = false;
    this.d.onChange();
  }

  /** A SYSTEM chat line (chatter.cpp AddChat('', text, true)) — added to the shared
   *  chat overlay's log with NO blink (system messages never blink; chatter.cpp:19-20). */
  private pushSystemLine(text: string): void {
    this.chatter?.AddChat('', text, true);
    this.d.onChange();
  }

  // ── ILobbyCallback ──────────────────────────────────────────────────────────
  OnLurkerCount(count: number): void {
    this.lurkerCount = count;
    this.d.onChange();
  }
  OnAddRoom(name: string, roomid: number, priv: boolean, cPlayers: number, cGames: number): void {
    this.rooms.set(roomid, { roomid, name, priv, cPlayers, cGames });
    this.d.onChange();
  }
  OnRemoveRoom(roomid: number): void {
    if (this.rooms.delete(roomid)) this.d.onChange();
  }
  OnUpdateRoom(roomid: number, cPlayers: number, cGames: number): void {
    const r = this.rooms.get(roomid);
    if (r !== undefined) {
      r.cPlayers = cPlayers;
      r.cGames = cGames;
      this.d.onChange();
    }
  }

  // ── IRoomCallback ───────────────────────────────────────────────────────────
  OnAddGame(
    player: string,
    gameid: number,
    params: GameParams,
    minplayers: number,
    maxplayers: number,
    title: string,
    _ctotal: number,
  ): void {
    this.games.set(gameid, {
      roomid: this.currentRoom?.roomid ?? 0,
      gameid,
      params,
      minplayers,
      maxplayers,
      title,
      creator: player,
      playing: false,
      anames: [],
    });
    this.d.onChange();
  }
  OnRemoveGame(gameid: number, _ctotal: number): void {
    if (this.games.delete(gameid)) this.d.onChange();
  }
  OnGameInProgress(gameid: number): void {
    const g = this.games.get(gameid);
    if (g !== undefined) {
      g.playing = true;
      this.d.onChange();
    }
  }
  OnGamePlayerNames(gameid: number, _cnames: number, anames: string[]): void {
    const g = this.games.get(gameid);
    if (g !== undefined) {
      g.anames = anames.slice();
      this.d.onChange();
    }
  }
  // roomform.cpp:501-509 — a player entered the room; system line once the
  // initial snapshot is in (status_ false).
  OnAddPlayer(player: string): void {
    this.roomPlayers.push(player);
    if (this.roomStatusComplete) this.pushSystemLine(`${player} entered the room.`);
    else this.d.onChange();
  }

  // roomform.cpp:511-543 — a player left; system line unless they're a game
  // creator (a game-create/join move handles its own notice) or are joining a
  // game (knLeaveRoomHintJoinGame === 1).
  OnRemovePlayer(hint: number, player: string): void {
    const i = this.roomPlayers.indexOf(player);
    if (i >= 0) this.roomPlayers.splice(i, 1);
    let add = true;
    for (const g of this.games.values()) {
      if (player === g.creator) {
        add = false;
        break;
      }
    }
    if (hint === knLeaveRoomHintJoinGame) add = false;
    if (add && this.roomStatusComplete) this.pushSystemLine(`${player} left the room.`);
    else this.d.onChange();
  }

  // roomform.cpp:549-559 — the initial room snapshot is delivered; from now on
  // callbacks are CHANGES (chat-worthy). Emit the "entered the … game room" line.
  OnStatusComplete(): void {
    this.roomStatusComplete = true;
    const here = this.roomPlayers[0] ?? this.playerName;
    const room = this.currentRoom?.name ?? '';
    this.pushSystemLine(`${here} has entered the ${room} game room. ${this.roomPlayersString()}`);
  }

  // roomform.cpp:561-583 — "No other players…/X is also…/A, B, and C are also…"
  private roomPlayersString(): string {
    const p = this.roomPlayers;
    if (p.length <= 1) return 'No other players are in the room.';
    if (p.length === 2) return `${p[1]} is also in the room.`;
    let s = '';
    for (let i = 1; i < p.length; i++) {
      s += i !== p.length - 1 ? `${p[i]}, ` : `and ${p[i]} `;
    }
    return s + 'are also in the room.';
  }

  // gameform.cpp:468-521 OnPlayersUpdate — diff the staging roster's "readies"
  // (fulfilled, non-computer rows) old→new and emit join/leave/READY system lines
  // with the side color name (GetColorName), exactly as the canvas GameForm does.
  private announceStagingChanges(players: PlayerRecord[]): void {
    const readiesOf = (
      recs: PlayerRecord[],
    ): { name: string; side: number; ready: boolean; bot: boolean }[] =>
      recs
        .filter((p) => (p.wf & (kfPlrrUnfulfilled | kfPlrrComputer)) === 0)
        .map((p) => ({
          name: p.name,
          side: p.side,
          ready: (p.wf & kfPlrrReady) !== 0,
          bot: (p.wf & kfPlrrBot) !== 0,
        }));
    const old = this.prevReadies.map((r) => ({
      name: r.name,
      side: r.side,
      ready: r.ready,
      bot: r.bot,
    }));
    const now = readiesOf(players);

    // a bot ("Bot N" seat) is announced as "Apex" (the bot brain); the raw name stays
    // the identity key for the join/leave/ready diff below — only the shown text changes.
    for (const nw of now) {
      if (!old.some((od) => od.name === nw.name)) {
        this.pushSystemLine(
          `${GetDisplayName(nw.name, nw.bot)} (${GetColorName(nw.side)}) joined this game.`,
        );
      }
    }
    for (const od of old) {
      if (!now.some((nw) => nw.name === od.name)) {
        this.pushSystemLine(
          `${GetDisplayName(od.name, od.bot)} (${GetColorName(od.side)}) left this game.`,
        );
      }
    }
    for (const nw of now) {
      let found = false;
      let ready = false;
      for (const od of old) {
        if (nw.name === od.name) {
          if (!od.ready && nw.ready) {
            ready = true;
            break;
          }
          found = true;
        }
      }
      if (!found && nw.ready) ready = true;
      if (ready)
        this.pushSystemLine(
          `${GetDisplayName(nw.name, nw.bot)} (${GetColorName(nw.side)}) is READY.`,
        );
    }

    this.prevReadies = now;
  }

  // ── shared chat (ILobby/IRoom/IGame all declare OnReceiveChat) ──────────────
  // game/lobbyform.cpp:383-386: the LOBBY has NO chat panel — an incoming line is
  // a transient message (the C++ kfMbKeepTimersEnabled msgbox), surfaced here as a
  // one-line notice. In a room/game it's a Chatter log line that blinks CHAT.
  OnReceiveChat(player: string, chat: string): void {
    if (this.screen === 'lobby') {
      this.lobbyChat.push(`${player}: ${chat}`);
      this.d.onChange();
      return;
    }
    // a room/game line → the shared Chatter/overlay (chatter.cpp AddChat(.., false), which
    // plays the sfx + blinks when the overlay is closed). The CHAT-button badge mirrors that:
    // a foreign line received while the overlay is closed is "unread".
    this.chatter?.AddChat(player, chat, false);
    if (player !== '' && player !== this.playerName && !(this.chatter?.IsChatting() ?? false)) {
      this.chatUnread = true;
    }
    this.d.onChange();
  }

  // ── IGameCallback (staging handshake) ───────────────────────────────────────
  OnNetMessage(nm: GameInner): void {
    if (nm.kind === 'gameparams') {
      // ScGameParams → CsPlayerJoin (+ creator auto-ready)
      this.serverParams = nm.params;
      this.tra?.SendNetMessage({
        nmid: NMID.CsPlayerJoin,
        kind: 'playerjoin',
        name: this.playerName.slice(0, kcbPlayerName - 1),
      });
      if (this.isCreator) this.tra?.SendNetMessage({ nmid: NMID.CsClientReady, kind: 'empty' });
    } else if (nm.kind === 'playersupdate') {
      // ScPlayersUpdate → roster
      this.lastPlayersUpdate = nm.players;
      this.gameRoster = rosterFromPlayersUpdate(nm.players);
      // a removed bot (its slot is now open) is gone from roster() → drop a stale selection so the
      // ADD/REMOVE button reverts to ADD OPPONENT BOT.
      if (
        this.selectedBotPid_ !== null &&
        !this.roster().some((p) => p.bot && p.pid === this.selectedBotPid_)
      ) {
        this.selectedBotPid_ = null;
      }
      this.announceStagingChanges(nm.players); // gameform.cpp:468-521 join/leave/READY lines
      this.d.onChange();
    } else if (nm.kind === 'begingame') {
      // ScBeginGame → run the sim
      this.gameSeed = nm.ulRandomSeed >>> 0;
      this.beginSimulation();
    } else if (nm.nmid === NMID.ScBeginGameFail) {
      this.status = 'Could not start the game — try again.';
      this.d.onChange();
    }
  }
  OnGameDisconnect(): void {
    // the game socket dropped — wind the transport back and re-join the room so
    // its CheckState(ROOM) actions work again (the canvas GameForm cancels out).
    this.tra?.SetGameCallback(null);
    this.isCreator = false;
    this.currentGame = null;
    this.status = 'Disconnected from the game.';
    void this.returnToRoom();
  }

  // ── BeginGame → runSimulation (the SAME seam the form path reaches) ─────────
  private beginSimulation(): void {
    if (this.tra === null || this.currentGame === null || this.chatter === null) return;
    const info: BeginGameInfo = {
      gameinfo: this.currentGame,
      creator: this.isCreator,
      wfRole: kfRoleMultiplayer | (this.isCreator ? kfRoleCreator : 0),
      chatter: this.chatter,
      handler: this.handler!,
      seed: this.gameSeed,
      playersUpdate: this.lastPlayersUpdate,
      roster: this.gameRoster,
      serverParams: this.serverParams,
      tra: this.tra,
    };
    // mount the mission pack around the sim (lobby.cpp BeginGame:341-347) so the
    // level data resolves for DLC/add-on MP packs (null-safe: stubbed in tests).
    const ml = this.d.deps.cgDeps.mlDeps;
    const packid = { id: info.gameinfo.params.packId, hash: info.gameinfo.params.packHash };
    if (ml?.packm !== undefined) ml.packm.Mount(ml.pakr, packid);
    this.inGame = true;
    this.d.onChange();
    this.d.deps.runSimulation(info, (_nGo) => {
      if (ml?.packm !== undefined) ml.packm.Unmount(ml.pakr, packid);
      this.inGame = false;
      this.status = '';
      this.tra?.SetGameCallback(null);
      this.isCreator = false;
      this.currentGame = null;
      // after the game, leave it then re-join the room (the Lobby.cpp roomLoop)
      void (async () => {
        if (this.tra !== null) await this.tra.LeaveGame();
        await this.returnToRoom();
      })();
    });
  }

  // ── helpers ─────────────────────────────────────────────────────────────────
  private fail(message: string): void {
    this.error = message;
    this.screen = 'error';
    this.status = '';
    this.d.onChange();
  }

  /** an Open() failure. A HANDSHAKE rejection (protocol mismatch) means this
   *  running build is older than the upgraded server (#206) — say so and let the
   *  view offer a one-tap RELOAD, instead of a generic connection failure. */
  private failOpen(rc: number): void {
    if (rc === knTransportOpenResultProtocolMismatch) {
      this.needsUpdate = true;
      this.fail('A new version of the game is available. Reload to update.');
      return;
    }
    this.fail('Could not connect to the server.');
  }

  /** game/dlmissionpack.cpp AskInstallMissionPack over the DOM seam: returns
   *  whether the pack is installed-and-current (and, for an upgrade, the adopted
   *  pack id). With no seam wired (node) every pack is treated as installed —
   *  the main pack always is, and a missing DLC pack simply can't be installed. */
  private async askInstallMissionPack(
    packid: PackId,
    uiTitle: string,
    packTitle: string | null,
  ): Promise<InstallMissionPackResult> {
    if (this.d.installMissionPack === undefined) return { installed: true };
    return this.d.installMissionPack(packid, uiTitle, packTitle);
  }

  private newHandler(): LoginHandler {
    const d = this.d.deps;
    return new LoginHandler({
      mp: d.mp,
      prefs: d.env.prefs,
      savePreferences: () => d.env.savePreferences(),
      httpGet: async (url) => (await d.httpGet(url)).body,
      deviceId: d.deviceId,
      platform: d.platform,
      authUrl: d.authUrl,
    });
  }

  /** Serverinfo fetch + the WEB filter (the ChooseServerForm.RequestInfos path,
   *  headless). Returns the usable, sort_key-ordered ServerInfo[] (the CONNECT
   *  step builds the transport), or null on no usable server / unreachable
   *  service (this.error carries the reason). */
  private async fetchServers(): Promise<ServerInfo[] | null> {
    const d = this.d.deps;
    const base = d.serverInfoUrl ?? kszServerInfoUrl;
    const url = `${base}?x=${PROTOCOL_CURRENT}&d=${queryEncode(d.deviceId)}&o=${queryEncode(d.platform)}`;
    const req = await d.httpGet(url);
    if (req.body === null) {
      this.error = req.error ?? 'The multiplayer service is unreachable.';
      return null;
    }
    let infos: ServerInfo[];
    try {
      const obj = JSON.parse(req.body) as { infos?: unknown };
      if (!Array.isArray(obj.infos)) return null;
      infos = (obj.infos as Record<string, unknown>[]).map((m) => ({
        sort_key: Number(m.sort_key ?? 0),
        name: String(m.name ?? ''),
        location: String(m.location ?? ''),
        address: String(m.address ?? ''),
        protocol: Number(m.protocol ?? 0),
        status: String(m.status ?? ''),
        player_count: Number(m.player_count ?? 0),
        type: String(m.type ?? ''),
        disallow: String(m.disallow ?? ''),
        websocket_proxy: String(m.websocket_proxy ?? ''),
      }));
    } catch {
      return null;
    }
    // the WEB filter (chooseserverform.RequestInfos): production-only (unless beta),
    // current protocol, must have a websocket proxy, not disallowed for web.
    const usable = infos
      .filter(
        (i) =>
          ((d.envBeta ?? false) || i.type === 'production') &&
          i.protocol >= PROTOCOL_CURRENT &&
          i.websocket_proxy.length > 0 &&
          !i.disallow.includes('web'),
      )
      .sort((a, b) => a.sort_key - b.sort_key);
    if (usable.length === 0) {
      this.error = 'No multiplayer servers are available.';
      return null;
    }
    return usable;
  }
}

// creategameform.cpp IndexFromMissionType — the Challenge|Add-On radio-tab split.
function categoryOfMissionType(mt: MissionType): MpMissionCategory {
  return mt === kmtMultiplayerAddOn ? 'addon' : 'challenge';
}

// creategameform.cpp:439-474 UpdateLabels — the kidcMissionPackInfo author/info
// string for the selected mission (the pack-info JSON's "<title> by <author>",
// else the pack name, with "by Spiffcode, Inc." appended for the main pack).
function missionPackInfo(ppim: PackInfoManagerSeam, packid: PackId, szPackName: string): string {
  const map = ppim.GetInfoMap(packid);
  if (map === null) {
    return packid.id >>> 0 === PACKID_MAIN ? `${szPackName} by Spiffcode, Inc.` : szPackName;
  }
  const szAuthor = PackInfoString(map, 'a') ?? '';
  const szTitle = PackInfoString(map, 't') ?? '';
  return `${szTitle} by ${szAuthor}`;
}

// game/roomform.cpp:23-49 RoomForm::ShowJoinMessage (lobbyform's InitiateJoinRoom
// shows the same strings) — the status text for a non-success CanJoinRoom result.
function roomJoinMessage(result: number): string {
  switch (result) {
    case 2:
      return 'The room is full. Try again later.'; // knRoomJoinResultFull
    case 3:
      return "Can't find this room. Try another room."; // knRoomJoinResultNotFound
    case knRoomJoinResultWrongPassword:
      return 'Incorrect password. Try again.';
    default:
      return 'Could not join the room.';
  }
}
